import { Component, type ReactNode, type ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';

interface PageErrorBoundaryProps {
  children: ReactNode;
  pageLabel?: string;
}

interface PageErrorBoundaryState {
  hasError: boolean;
}

class PageErrorBoundary extends Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
  constructor(props: PageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): PageErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const label = this.props.pageLabel ?? 'Page';
    console.error(`[${label}] Render error caught by PageErrorBoundary:`, error, errorInfo);
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        pageLabel: label,
      },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full py-20 px-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            This page couldn't load
          </h2>
          <p className="text-sm text-gray-500 mb-4 text-center max-w-sm">
            Something went wrong while loading this section. You can try again
            or navigate to a different page.
          </p>
          <button
            onClick={this.handleRetry}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default PageErrorBoundary;
